import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { SUPPORTED_LOCALES } from '@/contexts/LocaleContext';

const BASE_URL = 'https://masterclass.miselgroup.com';

export function CanonicalTag({ path = '' }) {
  const { locale } = useParams();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  const canonicalUrl = `${BASE_URL}/${locale || 'en'}${cleanPath}`;
  return (
    <Helmet>
      <link rel="canonical" href={canonicalUrl} />
    </Helmet>
  );
}

export function OGTags({ title, description, image, url, type = 'website' }) {
  const fullUrl = url?.startsWith('http') ? url : `${BASE_URL}${url || ''}`;
  const ogImage = image || `${BASE_URL}/og-default.jpg`;
  return (
    <Helmet>
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="MISEL GROUP MASTERCLASS" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}

export function PageSEO({ title, description, path, image, type }) {
  return (
    <>
      <Helmet>
        <title>{title}</title>
        {description && <meta name="description" content={description} />}
      </Helmet>
      <CanonicalTag path={path} />
      <HreflangTags path={path} />
      <OGTags title={title} description={description} image={image} url={path} type={type} />
    </>
  );
}

export function HreflangTags({ path = '' }) {
  const { locale } = useParams();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;

  return (
    <Helmet>
      {SUPPORTED_LOCALES.map(loc => (
        <link key={loc} rel="alternate" hrefLang={loc} href={`${BASE_URL}/${loc}${cleanPath}`} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${BASE_URL}/en${cleanPath}`} />
    </Helmet>
  );
}

export function StructuredData({ data }) {
  if (!data) return null;
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
}

export function buildOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "MISEL GROUP MASTERCLASS",
    "url": BASE_URL,
    "description": "International hair education academy where artistry meets precision.",
    "foundingDate": "1960",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Athens",
      "addressCountry": "GR"
    },
    "sameAs": [
      "https://www.facebook.com/miselgroup",
      "https://www.instagram.com/miselgroup",
      "https://www.youtube.com/miselgroup"
    ]
  };
}

export function buildCourseSchema(program, locale = 'en') {
  const loc = program.localization || {};
  const tuitionAmount = program.tuition_amount ? (program.tuition_amount / 100).toFixed(2) : program.tuition?.toString();
  const startDate = program.start_dates?.[0]?.date;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": loc.title || program.title,
    "description": loc.intro_statement || loc.subtitle || program.subtitle || '',
    "provider": {
      "@type": "EducationalOrganization",
      "name": "MISEL GROUP MASTERCLASS",
      "url": BASE_URL,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Athens",
        "addressCountry": "GR"
      }
    },
    "url": `${BASE_URL}/${locale}/programs/${program.slug}`,
    "inLanguage": locale,
    ...(program.hero_media?.url ? { "image": program.hero_media.url } : {}),
  };

  if (program.duration_weeks || program.duration) {
    schema.hasCourseInstance = {
      "@type": "CourseInstance",
      "courseMode": "onsite",
      ...(program.duration_weeks ? { "duration": `P${program.duration_weeks}W` } : {}),
      ...(startDate ? { "startDate": startDate } : {}),
      "locationCreated": {
        "@type": "Place",
        "name": "MISEL GROUP MASTERCLASS Academy",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Athens",
          "addressCountry": "GR"
        }
      }
    };
  }

  if (tuitionAmount) {
    schema.offers = {
      "@type": "Offer",
      "price": tuitionAmount,
      "priceCurrency": program.tuition_currency || "EUR",
      "availability": "https://schema.org/InStock",
      "url": `${BASE_URL}/${locale}/programs/${program.slug}`,
    };
  }

  if (loc.what_you_learn) {
    schema.about = loc.what_you_learn;
  }

  if (loc.who_is_it_for) {
    schema.audience = {
      "@type": "EducationalAudience",
      "educationalRole": "student",
      "audienceType": loc.who_is_it_for
    };
  }

  return schema;
}

export function buildArticleSchema(article, locale = 'en') {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt || '',
    "url": `${BASE_URL}/${locale}/magazine/${article.slug}`,
    "datePublished": article.published_at || article.created_at,
    "author": {
      "@type": "Organization",
      "name": "MISEL GROUP MASTERCLASS"
    },
    ...(article.featured_image ? { "image": article.featured_image } : {}),
    "publisher": {
      "@type": "Organization",
      "name": "MISEL GROUP MASTERCLASS",
      "url": BASE_URL
    }
  };
}

export function buildFAQSchema(items) {
  if (!items?.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": items.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };
}

export function buildBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      ...(item.url ? { "item": item.url } : {})
    }))
  };
}

export function buildPersonSchema(educator) {
  const en = educator.content?.en || {};
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": educator.name,
    "jobTitle": educator.role,
    "description": en.bio || '',
    "worksFor": {
      "@type": "EducationalOrganization",
      "name": "MISEL GROUP MASTERCLASS"
    },
    ...(educator.photo_url ? { "image": educator.photo_url } : {})
  };
}

export function buildReviewSchema(testimonials) {
  if (!testimonials?.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "MISEL GROUP MASTERCLASS",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "bestRating": "5",
      "reviewCount": testimonials.length.toString()
    },
    "review": testimonials.map(t => ({
      "@type": "Review",
      "author": { "@type": "Person", "name": t.author_name },
      "reviewBody": t.testimonial_text || t.quote,
      "reviewRating": { "@type": "Rating", "ratingValue": "5", "bestRating": "5" }
    }))
  };
}
